import RiskScoreTag from 'components/atoms/tags/riskScoreTag';
import { useTranslation } from 'react-i18next';
import BaseLoader from 'components/atoms/base-loader';
import classNames from 'classnames';
import { isUndefined } from 'lodash';
import ContactAvailabilityCombinedForm from 'components/molecules/contact-availability/combined-form';
import EditProvider from 'infrastructure/providers/edit-provider';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import useUser from 'utils/useUser';
import { CroStatuses } from 'infrastructure/enums';

import { useActionButtons } from '../../hooks';
import s from './styles.module.scss';
import { ActionDropdownButton } from '../action-dropdown-button';
import PatientStatusRow from '../patient-status-row';
import ConditionsRow from '../condition-row';
import Services from '../services';
import Title from '../title';
import Badges from '../badges';

import type { DoNotCallRestrictionType } from 'infrastructure/enums';
import type {
  ICondition,
  IPatientHoldingStatus,
} from 'infrastructure/interfaces';
import type { FC, ReactElement } from 'react';

interface CombinePatientBannerProps {
  patientGuid: string;
  showHoldingStatusBadge: boolean;
  showTemporaryHoldBadge?: boolean;
  isLoading: boolean;
  holdingStatus?: IPatientHoldingStatus;
  remainingDays: number;
  birthDate: string;
  fullName: string;
  statusColor?: string;
  rpmDoNotCall: boolean;
  ccmDoNotCall: boolean;
  rpmRestrictionType?: DoNotCallRestrictionType | null;
  ccmRestrictionType?: DoNotCallRestrictionType | null;
  showSalesForceIntegration?: boolean;
  isShowEmailTag?: boolean;
  pcmStatus?: boolean;
  sla?: number;
  providerServiceType?: string;
  serviceDeliveryModel?: string;
  physician?: ReactElement | string;
  referralTypes?: string;
  rpmStatusObj?: Readonly<{
    label: string;
    color: string;
  }>;
  ccmStatusObj?: Readonly<{
    label: string;
    color: string;
  }>;
  riskScore?: number;
  disabledCreateInternalAdmissionForms?: boolean;
  conditions?: ICondition[];
  careAgentName?: string;
  patientDetailsLoading?: boolean;
  conditionLoading?: boolean;
  showConsumablesBadge?: boolean;
  consumablesRemainingDays?: number;
  clinicName?: ReactElement | string;
  patientTZ?: string | null;
  apcmStatusObj?: Readonly<{
    label: string;
    color: string;
  }>;
  croStatus?: CroStatuses;
}

const CombinePatientBanner: FC<CombinePatientBannerProps> = (props) => {
  const {
    patientGuid,
    showHoldingStatusBadge,
    showTemporaryHoldBadge,
    isLoading,
    holdingStatus,
    remainingDays,
    birthDate,
    fullName,
    statusColor,
    rpmDoNotCall,
    ccmDoNotCall,
    rpmRestrictionType,
    ccmRestrictionType,
    showSalesForceIntegration,
    isShowEmailTag,
    pcmStatus,
    sla,
    providerServiceType,
    serviceDeliveryModel,
    physician,
    referralTypes,
    rpmStatusObj,
    ccmStatusObj,
    riskScore,
    conditions,
    careAgentName,
    conditionLoading,
    patientDetailsLoading,
    showConsumablesBadge,
    consumablesRemainingDays,
    disabledCreateInternalAdmissionForms,
    clinicName,
    patientTZ,
    apcmStatusObj,
    croStatus,
  } = props;
  const { combinedActionDropdownButtons } = useActionButtons({
    patientGuid,
    disabledCreateInternalAdmissionForms,
  });

  const { isAgencyDoctor } = useUser();
  const { t } = useTranslation();
  const { isReadOnlyRole } = useCheckRoleAccess();
  const preparedPatientTZ =
    patientTZ && patientTZ.split(' ')[0]?.replace(/[()]/g, '');

  const showCROBadge =
    croStatus && croStatus !== CroStatuses.False && isAgencyDoctor;

  return (
    <div
      data-cy="patient-header"
      className={classNames(s.wrapper, {
        [s.corner]: showHoldingStatusBadge || showTemporaryHoldBadge,
      })}
      id="patient-banner"
    >
      {patientDetailsLoading || conditionLoading || isLoading ? (
        <div className={s['loading-box']}>
          <BaseLoader loading={isLoading} />
        </div>
      ) : (
        <>
          <div
            className={s['status-indicator']}
            style={{ backgroundColor: statusColor }}
          />
          <Badges
            showCroBadge={showCROBadge}
            remainingDays={remainingDays}
            showHoldingStatusBadge={showHoldingStatusBadge}
            holdingStatus={holdingStatus}
            showTemporaryHoldBadge={showTemporaryHoldBadge}
            showConsumablesBadge={showConsumablesBadge}
            consumablesRemainingDays={consumablesRemainingDays}
          />
          <div className={s['wrapper-left']}>
            <Title
              fullName={fullName}
              birthDate={birthDate}
              pcmStatus={pcmStatus}
              isShowEmailTag={isShowEmailTag}
              rpmDoNotCall={rpmDoNotCall}
              ccmDoNotCall={ccmDoNotCall}
              rpmRestrictionType={rpmRestrictionType}
              ccmRestrictionType={ccmRestrictionType}
              showSalesForceIntegration={showSalesForceIntegration}
              sla={sla}
              patientGuid={patientGuid}
            />
            <Services
              careAgentName={careAgentName}
              physician={physician}
              providerServiceType={providerServiceType}
              referralTypes={referralTypes}
              serviceDeliveryModel={serviceDeliveryModel}
              clinicName={clinicName}
            />
          </div>
          <div className={s['wrapper-right']}>
            <div className={s['buttons-wrapper']}>
              {combinedActionDropdownButtons.map((b) => (
                <ActionDropdownButton
                  key={b.key}
                  options={b.options}
                  label={b.label}
                  dataCy={b.dataCy}
                  loading={b.loading}
                  disabled={b.disabled}
                />
              ))}
            </div>
            <div
              className={classNames(s.status, {
                [s.reverse]: false,
              })}
            >
              <PatientStatusRow
                isActiveLabel
                isActiveValue
                label={t('labels.rpmStatus')}
                value={rpmStatusObj?.label ?? 'Invalid Status'}
                color={rpmStatusObj?.color}
              />
              <PatientStatusRow
                isActiveLabel
                isActiveValue
                label={t('labels.ccmStatus')}
                value={ccmStatusObj?.label ?? 'Invalid Status'}
                color={ccmStatusObj?.color}
              />
              <PatientStatusRow
                isActiveLabel
                isActiveValue
                label={t('labels.apcmStatus')}
                value={apcmStatusObj?.label ?? 'Invalid Status'}
                color={apcmStatusObj?.color}
              />
            </div>
            <div className={s['risk-condition-wrapper']}>
              <ConditionsRow conditions={conditions} />
              {!isUndefined(riskScore) && <RiskScoreTag score={riskScore} />}
            </div>
          </div>
          <div
            className={s['wrapper-footer']}
            data-cy="banner-contactAvailability-footer"
          >
            <EditProvider>
              <ContactAvailabilityCombinedForm
                patientGuid={patientGuid}
                mode="banner"
                disableBannerEditBtn={isReadOnlyRole}
                headerAddon={
                  preparedPatientTZ && (
                    <p
                      className={s['timezone-label']}
                      data-cy="banner-timezone"
                    >
                      {t('labels.timezone')}: {preparedPatientTZ}
                    </p>
                  )
                }
              />
            </EditProvider>
          </div>
        </>
      )}
    </div>
  );
};
export default CombinePatientBanner;
